/**
 * AGP — bespoke Policies page (2026 redesign handoff, Page 5): page hero, a left
 * section rail that anchor-jumps into the content, and four numbered policy
 * blocks. Copy resolves through `useSiteContent(AGP_SITE)` (`policies.*`);
 * defaults are the handoff copy verbatim, bullets/bold via the rich-paras
 * micro-markup. Section ids/numbers stay code — the anchor rail depends on them.
 *
 * These are AGP's own published policies, not the platform's Terms/Privacy
 * version-acceptance texts (those live in tenant features and gate signup) — this
 * page is the public statement of them.
 */
import { AGP_SITE } from '@mlm/shared';
import { useSiteContent } from '../../../storefront/lib/site-content';
import { renderRichParas } from '../../../storefront/lib/rich-paras';
import { AgpShell } from './shell';

/** Anchor ids + numbering are structural; titles/bodies come from the manifest. */
const SECTIONS = [
  { id: 'refund', num: '01' },
  { id: 'terms', num: '02' },
  { id: 'shipment', num: '03' },
  { id: 'privacy', num: '04' },
];

export function AgpPolicies(): JSX.Element {
  const sc = useSiteContent(AGP_SITE);
  const policies = SECTIONS.map((s) => ({
    ...s,
    title: sc.text(`policies.${s.id}.title`),
    paras: sc.paras(`policies.${s.id}.body`),
  }));

  return (
    <AgpShell active="policies">
      <div className="agp-page-hero">
        <div className="agp-label">{sc.text('policies.hero.label')}</div>
        <div className="agp-rule centered" />
        <h1>{sc.text('policies.hero.title')}</h1>
        <p>{sc.text('policies.hero.sub')}</p>
      </div>

      <div className="agp-policies">
        <aside className="agp-policies-nav">
          <div className="agp-sidebar-heading">Sections</div>
          <ul>
            {policies.map((p) => (
              <li key={p.id}>
                <a href={`#${p.id}`}>{p.title}</a>
              </li>
            ))}
          </ul>
        </aside>

        <div className="agp-policies-content">
          {policies.map((p) => (
            <section key={p.id} id={p.id} className="agp-policy">
              <div className="agp-policy-num">{p.num}</div>
              <h2 className="agp-policy-title">{p.title}</h2>
              <div className="agp-policy-body">{renderRichParas(p.paras)}</div>
            </section>
          ))}
        </div>
      </div>
    </AgpShell>
  );
}
